import { useEffect, useMemo } from 'react';
import NSDropdown from 'bricks/NSDropdown/NSDropdown';
import { IOptionAnyValue } from 'bricks/types';
import { useGetProFormasByDeal } from 'queries/ProForma/useGetProFormasByDeal';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { Link } from 'react-router-dom';

export interface IProFormaSelectorProps {
    dealId: number;
    proFormaId?: number;
    onChangeProFormaSelection: (newSelection: IOptionAnyValue) => void;
    dropdownClassName?: string;
    showDefaultOption?: boolean;
    isLoading?: boolean;
    // Pro forma model ids to exclude from the options (e.g. models not yet supported by a given report).
    excludeModelIds?: number[];
}

const ProFormaSelector = ({
    dealId, proFormaId, onChangeProFormaSelection, dropdownClassName, showDefaultOption = false, isLoading = false, excludeModelIds,
}: IProFormaSelectorProps) => {
    const { data: proFormas } = useGetProFormasByDeal(
        { dealId, proFormaStatusIds: [PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE, PRO_FORMA_STATUS.PROJECT_CREATED] },
    );

    const selectableProFormas = useMemo(
        () => (excludeModelIds?.length
            ? (proFormas ?? []).filter(proForma => proForma.proFormaModelId == null || !excludeModelIds.includes(proForma.proFormaModelId))
            : proFormas),
        [proFormas, excludeModelIds],
    );

    const proFormasOptions: IOptionAnyValue[] = useMemo(() => {
        if (selectableProFormas && selectableProFormas.length) {
            return selectableProFormas.map(proForma => ({
                label: proForma.name,
                value: proForma.id,
            }));
        }

        return [
            {
                label: 'Create a pro forma first',
                component: (
                    <span>
                        <Link to={`/deal/${dealId}/pro_forma`} state={{ openCreateProForma: true }}>
                            Create a pro forma first
                        </Link>{' '}
                    </span>
                ),
                value: 'no-pro-forma',
            },
        ];
    }, [selectableProFormas, dealId]);

    const selectedProFormaOption = proFormasOptions?.find(proForma => proForma.value === proFormaId);

    useEffect(() => {
        if (showDefaultOption && selectableProFormas?.length && !selectedProFormaOption) {
            const activeProForma = selectableProFormas.find(proForma => proForma.isActive);
            if (activeProForma) {
                onChangeProFormaSelection({
                    label: activeProForma.name,
                    value: activeProForma.id,
                });
            } else {
                onChangeProFormaSelection(proFormasOptions[0]);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDefaultOption, proFormasOptions, selectedProFormaOption]);

    return (
        <div className="d-flex align-items-center">
            <NSDropdown
                containerClassName="mt-1 pr-1"
                options={proFormasOptions}
                selectedOption={selectedProFormaOption}
                handleSelectOption={onChangeProFormaSelection}
                placeholder="Select pro forma"
                isFullWidth
                toggleClassName={dropdownClassName}
            />
            {isLoading && <FontAwesomeIcon icon={faSpinner} />}
        </div>
    );
};

export default ProFormaSelector;
